import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import html2canvas from 'html2canvas'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api, getApiErrorMessage } from '../lib/api'
import {
  formatWon,
  latestThreeTaxYears,
  buildEstimatedClaimableBreakdown,
  failedTaxYearLabels,
  formatTaxYearLabels,
} from '../lib/hiddenInsuranceMoneyUtils'
import { formatApiDateTimeDisplay } from '../lib/date'
import { openCustomerCardNewsWindow } from '../lib/customerCardNews'
import { useToast } from '../ui/ToastProvider'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import HealthIntakeFormLayout from '../components/health/HealthIntakeFormLayout'
import HealthDataConsentFields from '../components/health/HealthDataConsentFields'
import HiddenInsuranceReportModal from '../components/hiddenInsurance/HiddenInsuranceReportModal'
import { useHealthDataConsentBundle } from '../hooks/useHealthDataConsentBundle'
import {
  HEALTH_DATA_CONSENT_VARIANTS,
  allHealthDataConsentsChecked,
  buildHealthDataConsentPayload,
  createInitialHealthDataConsents,
} from '../lib/healthDataConsent'
import FeatureBillingQuotaSlot from '../components/billing/FeatureBillingQuotaSlot'
import {
  FEATURE_BILLING_CODES,
  featureBillingBlockMessage,
  resolveFeatureBillingGate,
} from '../lib/featureBillingGate'

const SHOW_CUSTOMER_CARD_NEWS_BUTTONS = false

const MAX_IDENTITY_DIGITS = 13
const MAX_PHONE_DIGITS = 12
const AUTH_POLL_INTERVAL_MS = 5000
const AUTH_POLL_INITIAL_DELAY_MS = 8000
const AUTH_POLL_MAX_ATTEMPTS = 54
const HIDDEN_INSURANCE_SHARE_IMAGE_REPORT_CODE = 'HIDDEN_INSURANCE_CUSTOMER_IMAGE'
const HIDDEN_INSURANCE_REPORT_CODE = 'HIDDEN_INSURANCE_REPORT'

function formatPhoneDigitsKo(raw) {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '').slice(0, MAX_PHONE_DIGITS)
  if (!digits) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

function normalizeIdentityDigits(value) {
  return String(value ?? '').replace(/\D/g, '').slice(0, MAX_IDENTITY_DIGITS)
}

function formatIdentityDigitsForUi(identityDigits) {
  const id = normalizeIdentityDigits(identityDigits)
  if (!id.length) return ''
  if (id.length <= 6) return id
  return `${id.slice(0, 6)}-${id.slice(6)}`
}

function jobStatusBadgeClass(status) {
  if (status === 'COMPLETED') return 'text-emerald-700'
  if (status === 'FAILED') return 'text-red-700'
  if (status === 'ANALYSIS_RUNNING') return 'text-sky-700'
  if (status === 'AUTH_WAITING' || status === 'AUTH_REQUESTED') return 'text-amber-700'
  return 'text-slate-600'
}

function jobStatusLabel(status) {
  if (status === 'AUTH_REQUESTED') return '인증요청'
  if (status === 'AUTH_WAITING') return '인증 대기'
  if (status === 'ANALYSIS_RUNNING') return '분석 중'
  if (status === 'COMPLETED') return '조회 완료'
  if (status === 'FAILED') return '실패'
  return status || '-'
}

function formatErrorMessage(message) {
  const raw = String(message || '').trim()
  if (!raw) return '조회에 실패했습니다.'
  if (raw.startsWith('CODEF_AUTH_TIMEOUT:')) return '간편인증 대기 시간이 초과되었습니다.'
  if (raw.startsWith('CODEF_AUTH_FAILED:')) return `인증 확인 실패 (${raw.replace('CODEF_AUTH_FAILED:', '').trim()})`
  if (raw.startsWith('CODEF_AUTH_RECOVERY_FAILED:')) return `인증 완료 후 결과 회수 실패 (${raw.replace('CODEF_AUTH_RECOVERY_FAILED:', '').trim()})`
  if (raw.startsWith('CODEF_INITIAL_FAILED:')) return `조회 시작 실패 (${raw.replace('CODEF_INITIAL_FAILED:', '').trim()})`
  if (raw === 'IDENTITY_TTL_EXPIRED') return '인증 세션이 만료되었습니다. 다시 조회해 주세요.'
  if (raw === 'REQUEST_CONTEXT_EXPIRED') return '조회 세션이 만료되었습니다. 다시 조회해 주세요.'
  return raw
}

function parseLookupFailure(raw) {
  const s = String(raw || '').trim()
  if (!s) return { raw: '', phase: null, code: null, message: null, summary: null }
  const colonIdx = s.indexOf(':')
  const phase = colonIdx > 0 ? s.slice(0, colonIdx).trim() : null
  const tail = colonIdx > 0 ? s.slice(colonIdx + 1).trim() : s
  const code = (() => {
    const m = tail.match(/(CF-[A-Z0-9]+)/)
    return m ? m[1] : null
  })()
  return {
    raw: s,
    phase,
    code,
    message: formatErrorMessage(s),
    summary: formatErrorMessage(s),
  }
}

function safeDownloadNamePart(value) {
  const cleaned = String(value || '').trim().replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ')
  return cleaned || '고객'
}

function filenameFromContentDisposition(value, fallback) {
  const cd = String(value || '')
  const encoded = cd.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  if (encoded) {
    try {
      return decodeURIComponent(encoded)
    } catch {
      return encoded
    }
  }
  const quoted = cd.match(/filename="([^"]+)"/i)?.[1]
  return quoted || fallback
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

function hiddenInsuranceShareProfileFileId(user) {
  let storedUser = {}
  try {
    const raw = window?.localStorage?.getItem?.('auth.user')
    storedUser = raw ? JSON.parse(raw) : {}
  } catch {
    storedUser = {}
  }
  return (
    user?.profileReportImageFileId ||
    user?.profilePortraitCutoutFileId ||
    user?.profilePortraitImageFileId ||
    user?.profileImageFileId ||
    user?.profileCardImageFileId ||
    storedUser?.profileReportImageFileId ||
    storedUser?.profilePortraitCutoutFileId ||
    storedUser?.profilePortraitImageFileId ||
    storedUser?.profileImageFileId ||
    storedUser?.profileCardImageFileId ||
    ''
  )
}

async function downloadHiddenInsuranceShareImageApi({ jobId, customerName, filters, sortBy, reportTemplateId }) {
  const res = await api.get(`/medical-reimbursement-lookups/${jobId}/share-image`, {
    params: {
      generation: filters.generation,
      includeGlasses: !filters.excludeGlasses,
      excludePharmacyBelowThreshold: filters.excludePharmacyBelowThreshold,
      includeFamily: Boolean(filters.includeFamily),
      sortBy,
      reportTemplateId: reportTemplateId || undefined,
    },
    responseType: 'blob',
  })
  const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
  const fallback = `${safeDownloadNamePart(customerName)}_숨은보험금_고객발송.png`
  const filename = filenameFromContentDisposition(
    res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
    fallback
  )
  downloadBlob(blob, filename)
}

async function downloadHiddenInsuranceReportTemplateImageApi({ jobId, customerName, filters, sortBy, reportTemplateId }) {
  const res = await api.get(`/medical-reimbursement-lookups/${jobId}/report/template-image`, {
    params: {
      generation: filters.generation,
      includeGlasses: !filters.excludeGlasses,
      excludePharmacyBelowThreshold: filters.excludePharmacyBelowThreshold,
      includeFamily: Boolean(filters.includeFamily),
      sortBy,
      reportTemplateId: reportTemplateId || undefined,
    },
    responseType: 'blob',
  })
  const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
  const fallback = `${safeDownloadNamePart(customerName)}_숨은보험금_리포트.png`
  const filename = filenameFromContentDisposition(
    res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
    fallback
  )
  downloadBlob(blob, filename)
}

async function downloadHiddenInsuranceReportTemplateHtmlApi({ jobId, customerName, filters, sortBy, reportTemplateId }) {
  const res = await api.get(`/medical-reimbursement-lookups/${jobId}/report/template-html`, {
    params: {
      generation: filters.generation,
      includeGlasses: !filters.excludeGlasses,
      excludePharmacyBelowThreshold: filters.excludePharmacyBelowThreshold,
      includeFamily: Boolean(filters.includeFamily),
      sortBy,
      reportTemplateId: reportTemplateId || undefined,
    },
    responseType: 'blob',
  })
  const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'text/html' })
  const fallback = `${safeDownloadNamePart(customerName)}_숨은보험금_리포트.html`
  const filename = filenameFromContentDisposition(
    res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
    fallback
  )
  downloadBlob(blob, filename)
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('이미지 변환에 실패했습니다.'))
    }, 'image/png')
  })
}

async function loadFileDataUrl(fileId) {
  if (!fileId) return ''
  const res = await api.get(`/files/${encodeURIComponent(fileId)}/preview`, { responseType: 'blob' })
  const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
  return blobToDataUrl(blob)
}

function hiddenInsuranceShareImageHtml({
  customerName,
  targetYears,
  profileDataUrl,
  advisorName,
  advisorPhone,
  guide,
  totals,
  possibilityLabel,
  generationLabel,
}) {
  const years = Array.isArray(targetYears) && targetYears.length ? targetYears.join(', ') : '최근 3개년'
  const displayCustomer = customerName || '고객'
  const advisor = [advisorName, advisorPhone].filter(Boolean).join('  ') || '설계 EZ'
  const medicalExpense = formatWon(totals?.medicalExpense || guide?.medicalTotal || 0)
  const estimatedClaimable = formatWon(guide?.estimatedClaimable || 0)
  const reimbursementTotal = formatWon(guide?.reimbursementTotal || 0)
  const unclaimedHint = formatWon(guide?.unclaimedHint || 0)
  const ratio = guide?.claimRatio == null || Number.isNaN(Number(guide.claimRatio)) ? '-' : `${Number(guide.claimRatio).toFixed(1)}%`
  const profile = profileDataUrl
    ? `<img class="hero-person" src="${profileDataUrl}" alt="" />`
    : `<div class="hero-person fallback">${escapeHtml((advisorName || '설').slice(0, 1))}</div>`

  return `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
<style>
*{box-sizing:border-box} body{margin:0;background:#e8e5ed;font-family:"Noto Sans KR","Nanum Gothic",Arial,sans-serif;color:#101728}.share{position:relative;width:900px;height:1260px;overflow:hidden;background:#e8e5ed;padding:38px 18px 10px}.rule{position:absolute;left:55px;width:230px;height:2px;background:#2b3695}.rule.r1{top:40px}.rule.r2{top:88px}.eyebrow{position:absolute;left:72px;top:53px;font-size:25px;font-weight:800;color:#283391}.headline{position:absolute;left:54px;top:92px;font-size:82px;line-height:1.24;font-weight:900;letter-spacing:0;color:#202981}.headline span{display:block}.sub{position:absolute;left:55px;top:336px;font-size:20px;font-weight:800;color:#ff714c}.hero-person{position:absolute;right:140px;top:34px;width:260px;height:338px;object-fit:contain;object-position:bottom center}.hero-person.fallback{display:flex;align-items:center;justify-content:center;border-radius:28px;background:rgba(255,255,255,.55);font-size:80px;font-weight:900;color:#27338f}.bubble{position:absolute;right:20px;top:116px;width:132px;height:88px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;text-align:center;font-size:15px;line-height:1.38;font-weight:900}.bubble:after{content:"";position:absolute;left:46px;bottom:-24px;border-width:27px 13px 0 13px;border-style:solid;border-color:#fff transparent transparent transparent;transform:rotate(22deg)}.bubble b{color:#27338f}.features{position:absolute;left:18px;top:372px;width:864px;height:118px;border-radius:10px;background:#27338f;display:grid;grid-template-columns:1fr 1fr 1fr;color:#fff}.feature{position:relative;display:flex;align-items:center;justify-content:center;gap:18px;font-size:27px;line-height:1.28;font-weight:900}.feature+.feature{border-left:2px solid rgba(255,255,255,.45)}.feature svg{width:58px;height:58px;stroke:#fff;stroke-width:5;fill:none;flex:0 0 auto}.panel{position:absolute;left:18px;top:500px;width:864px;height:506px;border-radius:14px;background:#fff;padding:17px 28px}.brand-row{display:flex;justify-content:space-between;align-items:center;font-size:22px;font-weight:900}.brand{color:#27338f}.result-head{margin-top:14px;height:60px;border-radius:8px;background:#3d52d7;color:#fff;padding:13px 16px}.result-head .main{font-size:16px;font-weight:900}.result-head .small{margin-top:4px;font-size:11px;color:#dbe5ff}.metrics3{margin-top:20px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}.metric{min-height:72px;border:1px solid #dde5ef;border-radius:7px;padding:10px 12px;background:#fff}.metric .label{font-size:12px;font-weight:800;color:#697386}.metric .value{margin-top:4px;font-size:25px;line-height:1.05;font-weight:900;letter-spacing:0;color:#101728}.metric .hint{margin-top:5px;font-size:10px;color:#778198}.metrics5{margin-top:22px;display:grid;grid-template-columns:132px 168px 168px 160px 1fr;gap:10px}.possibility{border-color:#f3c4ca;background:#fff5f6;text-align:center}.possibility .label{font-size:13px;color:#992e3f}.possibility .value{margin-top:15px;white-space:nowrap;font-size:30px;color:#992e3f}.notice{position:absolute;left:46px;right:28px;bottom:70px;border-radius:8px;background:#fff8df;padding:12px 18px;color:#5c4910}.notice .n1{font-size:13px;font-weight:900}.notice .n2{margin-top:4px;font-size:10px}.cta{position:absolute;left:20px;top:1014px;width:423px;height:136px;border-radius:12px;background:#27338f;color:#fff}.cta.r{left:447px;width:433px}.cta svg{position:absolute;left:28px;top:24px;width:80px;height:88px;stroke:#fff;stroke-width:5;fill:none}.cta .t1{position:absolute;left:132px;top:15px;font-size:28px;line-height:1.28;font-weight:900}.cta .t2{position:absolute;left:132px;top:78px;font-size:18px;line-height:1.45;font-weight:900;color:#ff714c}.cta.r svg{left:34px}.cta.r .t1,.cta.r .t2{left:112px}.footer{position:absolute;left:20px;bottom:10px;width:860px;height:90px;border-radius:9px;background:#7f95eb;text-align:center}.footer .advisor{margin-top:12px;font-size:31px;font-weight:900;color:#182270}.footer .msg{margin-top:5px;font-size:21px;font-weight:900;color:#ffd64b}.footer .msg span{color:#fff}
</style>
</head>
<body>
<div class="share">
  <div class="rule r1"></div><div class="rule r2"></div>
  <div class="eyebrow">나도 몰래 잠들어 있는</div>
  <div class="headline"><span>숨은 보험금</span><span>찾아드립니다.</span></div>
  <div class="sub">실손보험 청구, 깜빡하고 놓친 보험금이 생각보다 많습니다.</div>
  ${profile}
  <div class="bubble"><div>내 보험금<br/><b>지금 바로</b><br/>확인해 보세요!</div></div>
  <div class="features">
    <div class="feature"><svg viewBox="0 0 64 64"><circle cx="25" cy="25" r="18"/><path d="M39 39 57 57"/><path d="M17 25h16M21 17l4 22 8-22"/></svg><div>숨은보험금<br/>간편조회</div></div>
    <div class="feature"><svg viewBox="0 0 64 64"><rect x="15" y="10" width="36" height="46"/><path d="M24 8h18M23 25h16M23 37h14M43 42l10-10 5 5-10 10-8 3z"/></svg><div>최근 3년<br/>병원 진료 내역기반</div></div>
    <div class="feature"><svg viewBox="0 0 64 64"><path d="M9 45c12 0 17-10 27-10h14"/><path d="M12 50h39"/><circle cx="43" cy="18" r="13"/><path d="M36 18h14M43 10v20"/></svg><div>청구 가능금액<br/>한눈에 확인</div></div>
  </div>
  <section class="panel">
    <div class="brand-row"><div>숨은보험금찾기</div><div class="brand">설계 EZ</div></div>
    <div class="result-head"><div class="main">${escapeHtml(displayCustomer)}님의 숨은보험금 조회 결과</div><div class="small">국세청 연말정산 간소화 자료(${escapeHtml(years)}) 기준입니다.</div></div>
    <div class="metrics3">
      <div class="metric"><div class="label">의료비 합계</div><div class="value">${escapeHtml(medicalExpense)}</div><div class="hint">최근 3개년 기준</div></div>
      <div class="metric"><div class="label">추정 청구 가능액</div><div class="value">${escapeHtml(estimatedClaimable)}</div><div class="hint">세대별 공제 반영</div></div>
      <div class="metric"><div class="label">실손 수령 합계</div><div class="value">${escapeHtml(reimbursementTotal)}</div><div class="hint">국세청 수령자료 기준</div></div>
    </div>
    <div class="metrics5">
      <div class="metric possibility"><div class="label">숨은보험금 가능성</div><div class="value">${escapeHtml(possibilityLabel || '-')}</div></div>
      <div class="metric"><div class="label">미청구 가능성</div><div class="value">${escapeHtml(unclaimedHint)}</div></div>
      <div class="metric"><div class="label">수령 비율</div><div class="value">${escapeHtml(ratio)}</div><div class="hint">${escapeHtml(generationLabel || '')}</div></div>
      <div class="metric"><div class="label">리포트 기준</div><div class="value">본인 자료</div><div class="hint">가족 자료는 기본 제외</div></div>
    </div>
    <div class="notice"><div class="n1">숨은보험금 가능성은 국세청 의료비 공제자료 기준의 참고 데이터입니다.</div><div class="n2">가족 카드나 가족 연말정산 자료에 잡힌 의료비는 고객 기준 조회에서 보이지 않을 수 있습니다.</div></div>
  </section>
  <div class="cta"><svg viewBox="0 0 90 90"><rect x="8" y="8" width="48" height="68" rx="5"/><path d="M20 29l14 14 27-29"/><circle cx="58" cy="64" r="23"/></svg><div class="t1">나는 얼마나<br/>돌려 받을 수 있을까?</div><div class="t2">간단한 동의만으로 숨은보험금을<br/>확인해보세요!</div></div>
  <div class="cta r"><svg viewBox="0 0 90 90"><circle cx="37" cy="37" r="30"/><path d="M59 59l26 26"/><path d="M20 34c7-14 27-17 39-4"/></svg><div class="t1">지금 바로 숨은보험금을<br/>무료로 확인해 보세요!</div><div class="t2">무료 조회 및 분석으로<br/>꼼꼼하게 찾아드립니다</div></div>
  <div class="footer"><div class="advisor">${escapeHtml(advisor)}</div><div class="msg">'나도 한번 확인해 보고 싶다' <span>하시는 분 계시면 함께 소개해주세요!</span></div></div>
</div>
</body>
</html>`
}

// eslint-disable-next-line no-unused-vars
async function downloadHiddenInsuranceShareImageHtml({ customerName, targetYears, presentation, profileFileId, advisor }) {
  const profileDataUrl = await loadFileDataUrl(profileFileId)
  const guide = presentation?.guide || {}
  const totals = presentation?.totals || {}
  const possibilityLabel = hiddenInsurancePossibilityLabel(guide.claimRatio)
  const generations = presentation?.generations || []
  const generationLabel = generations.find((g) => g.value === presentation?.selectedGenerationBasis?.generation)?.label
    || presentation?.selectedGenerationBasis?.generationLabel
    || presentation?.generationLabel
    || ''

  const wrapper = document.createElement('div')
  wrapper.style.position = 'fixed'
  wrapper.style.left = '-10000px'
  wrapper.style.top = '0'
  wrapper.style.width = '900px'
  wrapper.style.height = '1260px'
  wrapper.innerHTML = hiddenInsuranceShareImageHtml({
    customerName,
    targetYears,
    profileDataUrl,
    advisorName: advisor?.name,
    advisorPhone: advisor?.phone,
    guide,
    totals,
    possibilityLabel,
    generationLabel,
  })
  document.body.appendChild(wrapper)
  try {
    const node = wrapper.querySelector('.share')
    const img = wrapper.querySelector('.hero-person')
    if (img?.decode) {
      await img.decode().catch(() => {})
    }
    const canvas = await html2canvas(node, {
      backgroundColor: '#e8e5ed',
      scale: 2,
      width: 900,
      height: 1260,
      windowWidth: 900,
      windowHeight: 1260,
      useCORS: true,
      logging: false,
    })
    const blob = await canvasToBlob(canvas)
    downloadBlob(blob, `${safeDownloadNamePart(customerName)}_숨은보험금_고객발송.png`)
  } finally {
    wrapper.remove()
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', 'true')
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    return true
  } catch {
    return false
  }
}

const DEFAULT_FORM = {
  customerRefId: '',
  customerName: '',
  identity: '',
  phoneNumber: '',
  telecom: '0',
  authProvider: 'KAKAO',
}

const DEFAULT_FILTERS = {
  generation: 'GEN3',
  excludeGlasses: true,
  excludePharmacyBelowThreshold: true,
  includeFamily: false,
}

function parseDateSortKey(title, year) {
  const t = String(title || '').trim()
  const y = String(year || '').replace(/\D/g, '')
  const fullDate = t.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (fullDate) return `${fullDate[1]}${fullDate[2]}${fullDate[3]}`
  const monthLabel = t.match(/^(\d{4})년\s*(\d{1,2})월$/)
  if (monthLabel) return `${monthLabel[1]}${String(monthLabel[2]).padStart(2, '0')}00`
  if (y.length === 4) return `${y}0000`
  return '00000000'
}

function mergeMedicalAndReimbursementItems(medicalItems, reimbursementItems) {
  const merged = [
    ...medicalItems.map((item) => ({ ...item, isReimbursement: false })),
    ...reimbursementItems.map((item) => ({ ...item, isReimbursement: true })),
  ]
  merged.sort((a, b) => {
    const dateCmp = parseDateSortKey(b.title, b.year).localeCompare(parseDateSortKey(a.title, a.year))
    if (dateCmp !== 0) return dateCmp
    if (a.isReimbursement !== b.isReimbursement) return a.isReimbursement ? 1 : -1
    return String(b.year || '').localeCompare(String(a.year || ''))
  })
  return merged
}

function hiddenInsurancePossibilityLabel(ratio) {
  if (ratio == null || Number.isNaN(Number(ratio))) return '-'
  const r = Number(ratio)
  if (r < 20) return '매우 높음'
  if (r < 40) return '높음'
  if (r < 60) return '보통'
  if (r < 80) return '낮음'
  return '매우 낮음'
}

function hiddenInsurancePossibilityTone(ratio) {
  if (ratio == null || Number.isNaN(Number(ratio))) return 'text-slate-600 bg-slate-50 border-slate-200'
  const r = Number(ratio)
  if (r < 20) return 'text-rose-800 bg-rose-50 border-rose-200'
  if (r < 40) return 'text-orange-800 bg-orange-50 border-orange-200'
  if (r < 60) return 'text-amber-900 bg-amber-50 border-amber-200'
  if (r < 80) return 'text-sky-800 bg-sky-50 border-sky-200'
  return 'text-emerald-800 bg-emerald-50 border-emerald-200'
}

async function fetchPresentation(jobId, filters, sortBy) {
  const res = await api.get(`/medical-reimbursement-lookups/${jobId}/presentation`, {
    params: {
      generation: filters.generation,
      includeGlasses: !filters.excludeGlasses,
      excludePharmacyBelowThreshold: filters.excludePharmacyBelowThreshold,
      includeFamily: Boolean(filters.includeFamily),
      sortBy,
    },
  })
  return res.data?.data || res.data
}

async function normalizePresentationGuide(presentation) {
  const guide = { ...(presentation?.guide || {}) }
  const medicalTotal = Number(guide.medicalTotal ?? presentation?.totals?.medicalExpense) || 0
  const estimatedClaimable = Number(guide.estimatedClaimable) || 0

  guide.deductibleReductionTotal = Number.isFinite(Number(guide.deductibleReductionTotal))
    ? Number(guide.deductibleReductionTotal)
    : Math.max(0, medicalTotal - estimatedClaimable)
  guide.glassesExcludedTotal = Number(guide.glassesExcludedTotal) || 0
  guide.belowThresholdExcludedTotal = Number(guide.belowThresholdExcludedTotal) || 0
  guide.filterExcludedTotal = guide.glassesExcludedTotal + guide.belowThresholdExcludedTotal

  return { ...presentation, guide }
}

function SummaryCard({ label, amount, value, sub, toneClass, compact, emphasis, heroValue, className = '' }) {
  const labelCls = emphasis
    ? 'text-sm font-semibold text-slate-600'
    : compact
      ? 'text-[11px] leading-tight font-medium text-slate-500'
      : 'text-xs font-medium text-slate-500'
  const valueCls = heroValue
    ? 'mt-5 whitespace-nowrap text-center text-2xl font-extrabold leading-none tracking-tight sm:text-4xl lg:text-3xl xl:text-4xl'
    : emphasis
      ? 'mt-1 text-3xl font-extrabold tracking-tight'
      : compact
        ? 'mt-0.5 text-base font-bold'
        : 'mt-1 text-2xl font-bold text-slate-900'
  return (
    <div className={`rounded-xl border px-4 shadow-sm ${compact ? 'py-2.5' : emphasis ? 'py-4' : 'py-3'} ${toneClass || 'border-slate-200 bg-white'} ${className}`}>
      <div className={labelCls}>{label}</div>
      {value != null ? (
        <div className={valueCls}>{value}</div>
      ) : (
        <div className={`${valueCls} ${emphasis || heroValue ? '' : 'text-slate-900'}`}>{formatWon(amount)}</div>
      )}
      {sub ? <div className={`text-slate-500 ${emphasis ? 'mt-1 text-xs' : compact ? 'mt-0.5 text-[10px]' : 'mt-0.5 text-xs'}`}>{sub}</div> : null}
    </div>
  )
}

function listItemYearKey(item) {
  const title = String(item?.title || '').trim()
  const fromTitle = title.match(/^(\d{4})/)
  if (fromTitle) return fromTitle[1]
  const year = String(item?.year || '').replace(/\D/g, '')
  if (year.length >= 4) return year.slice(0, 4)
  return null
}

function ResultListItem({ item, compact = false }) {
  const isReimbursement = item.isReimbursement || item.category === 'reimbursements'
  const showReimbursementLabel = item.isReimbursement === true
  const textTone = isReimbursement ? 'text-blue-700' : 'text-slate-900'
  const mutedTone = isReimbursement ? 'text-blue-500' : 'text-slate-500'

  return (
    <div className={`rounded-lg border border-slate-200 bg-white shadow-sm ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}>
      <div className={`flex ${compact ? 'flex-col gap-1' : 'items-center justify-between gap-3'}`}>
        <div className="min-w-0">
          <div className={`truncate text-sm font-semibold ${textTone}`}>
            {showReimbursementLabel ? `[실손수령] ${item.title || '-'}` : (item.title || '-')}
          </div>
          <div className={`truncate text-xs ${mutedTone}`}>
            {[item.subtitle, item.personName ? `대상자: ${item.personName}` : null, item.institutionGradeLabel, item.visitTypeLabel, item.account]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className={`shrink-0 text-sm font-bold ${textTone}`}>{formatWon(item.amount)}</div>
      </div>
    </div>
  )
}

function ResultYearColumns({ items, years, emptyLabel }) {
  const normalizedYears = Array.from(new Set((years || []).map((year) => String(year).replace(/\D/g, '').slice(0, 4)).filter(Boolean)))
  const yearColumns = normalizedYears.length ? normalizedYears : Array.from(new Set(items.map(listItemYearKey).filter(Boolean)))

  if (!yearColumns.length) {
    return <ResultList items={items} emptyLabel={emptyLabel} sortBy="date" />
  }

  return (
    <div className="grid gap-3 lg:grid-cols-3">
      {yearColumns.map((year) => {
        const yearItems = items.filter((item) => listItemYearKey(item) === year)
        return (
          <section key={year} className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
              <div className="text-sm font-bold text-slate-800">{year}년</div>
              <div className="text-xs text-slate-500">{yearItems.length}건</div>
            </div>
            <div className="space-y-2 p-2">
              {yearItems.length ? (
                yearItems.map((item, idx) => (
                  <ResultListItem key={item.id || `${year}-${idx}-${item.title || 'item'}`} item={item} compact />
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-400">
                  내역 없음
                </div>
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function ResultList({ items, emptyLabel, sortBy = 'date', yearlyColumns = false, years = [] }) {
  if (!items.length) {
    return <div className="py-10 text-center text-sm text-slate-500">{emptyLabel}</div>
  }

  if (yearlyColumns) {
    return <ResultYearColumns items={items} years={years} emptyLabel={emptyLabel} />
  }

  let lastYear = null

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const yearKey = sortBy === 'date' ? listItemYearKey(item) : null
        const showYearDivider = yearKey && yearKey !== lastYear
        if (showYearDivider) lastYear = yearKey
        return (
          <React.Fragment key={item.id || `${yearKey}-${idx}`}>
            {showYearDivider ? (
              <div className="flex items-center gap-3 pt-2 first:pt-0">
                <div className="text-sm font-bold text-slate-800">{yearKey}년</div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            ) : null}
            <ResultListItem item={item} />
          </React.Fragment>
        )
      })}
    </div>
  )
}

function LookupResultView({
  job,
  customerName,
  onBackToList,
  showCardNews = false,
  showShareImageButton = false,
  canDownloadShareImage = false,
  shareProfileFileId = '',
  advisor = null,
}) {
  const { show } = useToast()
  const targetYears = job?.targetYears || latestThreeTaxYears()
  const [resultTab, setResultTab] = useState('medical')
  const sortBy = 'date'
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [reportOpen, setReportOpen] = useState(false)
  const [shareImageDownloading, setShareImageDownloading] = useState(false)
  const [shareImageTemplateId, setShareImageTemplateId] = useState('')
  const [reportTemplateDownloading, setReportTemplateDownloading] = useState(false)
  const [reportTemplateId, setReportTemplateId] = useState('')
  const [reportHtmlDownloading, setReportHtmlDownloading] = useState(false)
  const [reportHtmlTemplateId, setReportHtmlTemplateId] = useState('')

  const presentationQuery = useQuery({
    queryKey: ['hidden-insurance-presentation', job?.id, filters, sortBy],
    queryFn: async () => {
      const presentation = await fetchPresentation(job.id, filters, sortBy)
      return normalizePresentationGuide(presentation)
    },
    enabled: job?.id != null && job?.status === 'COMPLETED',
    staleTime: 5 * 60 * 1000,
  })
  const shareImageTemplatesQuery = useQuery({
    queryKey: ['hidden-insurance-share-image-templates'],
    queryFn: async () => {
      const res = await api.get('/ops/report-templates', {
        params: { reportCode: HIDDEN_INSURANCE_SHARE_IMAGE_REPORT_CODE },
      })
      const payload = res?.data?.data !== undefined ? res.data.data : res?.data
      return Array.isArray(payload) ? payload : []
    },
    enabled: Boolean(showShareImageButton),
    staleTime: 60 * 1000,
  })
  const reportTemplatesQuery = useQuery({
    queryKey: ['hidden-insurance-report-templates'],
    queryFn: async () => {
      const res = await api.get('/ops/report-templates', {
        params: { reportCode: HIDDEN_INSURANCE_REPORT_CODE },
      })
      const payload = res?.data?.data !== undefined ? res.data.data : res?.data
      return Array.isArray(payload) ? payload : []
    },
    enabled: Boolean(showShareImageButton),
    staleTime: 60 * 1000,
  })

  const presentation = presentationQuery.data
  const totals = presentation?.totals || {}
  const guide = presentation?.guide || {}
  const counts = presentation?.counts || {}
  const medicalItems = presentation?.lists?.medical || []
  const reimbursementItems = presentation?.lists?.reimbursements || []
  const listItems = resultTab === 'medical'
    ? mergeMedicalAndReimbursementItems(medicalItems, reimbursementItems)
    : (presentation?.lists?.[resultTab] || [])
  const generations = presentation?.generations || []
  const generationBasisList = presentation?.generationBasisSummaries || []
  const selectedBasis = presentation?.selectedGenerationBasis || {}
  const generationLabel = generations.find((g) => g.value === filters.generation)?.label || filters.generation
  const claimRatio = guide.claimRatio
  const possibilityLabel = hiddenInsurancePossibilityLabel(claimRatio)
  const possibilityTone = hiddenInsurancePossibilityTone(claimRatio)
  const claimableBreakdown = buildEstimatedClaimableBreakdown({ guide, filters, totals })
  const failedYearLabels = failedTaxYearLabels(presentation?.yearStatuses, presentation?.failedYears)
  const successfulYearLabels = formatTaxYearLabels(presentation?.successfulYears)
  const shareImageTemplateOptions = useMemo(() => {
    const rows = Array.isArray(shareImageTemplatesQuery.data) ? shareImageTemplatesQuery.data : []
    return rows.filter((item) => (
      item?.templateStatus === 'PUBLISHED'
      && item?.userSelectable
      && ['PNG_OVERLAY', 'HYBRID'].includes(item?.renderMode)
    ))
  }, [shareImageTemplatesQuery.data])
  const reportTemplateOptions = useMemo(() => {
    const rows = Array.isArray(reportTemplatesQuery.data) ? reportTemplatesQuery.data : []
    return rows.filter((item) => (
      item?.templateStatus === 'PUBLISHED'
      && ['PNG_OVERLAY', 'HYBRID'].includes(item?.renderMode)
    ))
  }, [reportTemplatesQuery.data])
  const reportHtmlTemplateOptions = useMemo(() => {
    const rows = Array.isArray(reportTemplatesQuery.data) ? reportTemplatesQuery.data : []
    return rows.filter((item) => (
      item?.templateStatus === 'PUBLISHED'
      && ['HTML', 'HYBRID'].includes(item?.renderMode)
    ))
  }, [reportTemplatesQuery.data])
  useEffect(() => {
    if (!shareImageTemplateId) return
    if (!shareImageTemplateOptions.some((template) => String(template.id) === String(shareImageTemplateId))) {
      setShareImageTemplateId('')
    }
  }, [shareImageTemplateId, shareImageTemplateOptions])
  useEffect(() => {
    if (!reportTemplateId) return
    if (!reportTemplateOptions.some((template) => String(template.id) === String(reportTemplateId))) {
      setReportTemplateId('')
    }
  }, [reportTemplateId, reportTemplateOptions])
  useEffect(() => {
    if (!reportHtmlTemplateId) return
    if (!reportHtmlTemplateOptions.some((template) => String(template.id) === String(reportHtmlTemplateId))) {
      setReportHtmlTemplateId('')
    }
  }, [reportHtmlTemplateId, reportHtmlTemplateOptions])
  const downloadShareImage = useCallback(async () => {
    if (!job?.id || shareImageDownloading || !canDownloadShareImage) return
    setShareImageDownloading(true)
    try {
      await downloadHiddenInsuranceShareImageApi({
        jobId: job.id,
        customerName,
        filters,
        sortBy,
        reportTemplateId: shareImageTemplateId,
      })
      show('고객발송 이미지를 다운로드했습니다.', 'success')
    } catch (err) {
      show(getApiErrorMessage(err, '고객발송 이미지 다운로드에 실패했습니다.'), 'error')
    } finally {
      setShareImageDownloading(false)
    }
  }, [canDownloadShareImage, customerName, filters, job?.id, shareImageDownloading, shareImageTemplateId, show, sortBy])
  const downloadReportTemplateImage = useCallback(async () => {
    if (!job?.id || reportTemplateDownloading || !showShareImageButton) return
    setReportTemplateDownloading(true)
    try {
      await downloadHiddenInsuranceReportTemplateImageApi({
        jobId: job.id,
        customerName,
        filters,
        sortBy,
        reportTemplateId,
      })
      show('템플릿 리포트 이미지를 다운로드했습니다.', 'success')
    } catch (err) {
      show(getApiErrorMessage(err, '템플릿 리포트 이미지 다운로드에 실패했습니다.'), 'error')
    } finally {
      setReportTemplateDownloading(false)
    }
  }, [customerName, filters, job?.id, reportTemplateDownloading, reportTemplateId, show, showShareImageButton, sortBy])
  const downloadReportTemplateHtml = useCallback(async () => {
    if (!job?.id || reportHtmlDownloading || !showShareImageButton) return
    setReportHtmlDownloading(true)
    try {
      await downloadHiddenInsuranceReportTemplateHtmlApi({
        jobId: job.id,
        customerName,
        filters,
        sortBy,
        reportTemplateId: reportHtmlTemplateId,
      })
      show('HTML 리포트를 다운로드했습니다.', 'success')
    } catch (err) {
      show(getApiErrorMessage(err, 'HTML 리포트 다운로드에 실패했습니다.'), 'error')
    } finally {
      setReportHtmlDownloading(false)
    }
  }, [customerName, filters, job?.id, reportHtmlDownloading, reportHtmlTemplateId, show, showShareImageButton, sortBy])
  const openCardNews = () => {
    const opened = openCustomerCardNewsWindow({
      sourceType: 'hidden',
      customer: { customerName },
      latestHiddenInsurance: {
        id: job?.id,
        status: job?.status,
        createdAt: job?.createdAt,
        createdAtDisplay: job?.createdAtDisplay || job?.createdAt,
        estimatedClaimable: guide.estimatedClaimable,
        reimbursementTotal: guide.reimbursementTotal,
        unclaimedHint: guide.unclaimedHint,
      },
    })
    if (!opened) {
      window.alert('팝업 차단을 해제한 뒤 다시 시도해주세요.')
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-brand-navy px-5 py-5 text-white">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{customerName || '고객'}님의 숨은보험금 조회 결과</h2>
            <p className="mt-1 text-xs text-sky-100">
              국세청 연말정산 간소화 자료(최근 {targetYears.join(', ')}년) 기준입니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {showShareImageButton ? (
              <>
                {shareImageTemplateOptions.length > 0 ? (
                  <select
                    className="max-w-[220px] rounded border border-white/30 bg-brand-navy px-2 py-1.5 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                    value={shareImageTemplateId}
                    onChange={(e) => setShareImageTemplateId(e.target.value)}
                    disabled={shareImageDownloading}
                    title="운영자 전용 발송 이미지 템플릿"
                  >
                    <option value="">운영 기본 템플릿</option>
                    {shareImageTemplateOptions.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.effectiveSelectionLabel || template.title}
                      </option>
                    ))}
                  </select>
                ) : null}
                <button
                  type="button"
                  className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={downloadShareImage}
                  disabled={!canDownloadShareImage || presentationQuery.isLoading || presentationQuery.isError || shareImageDownloading}
                  title={canDownloadShareImage ? undefined : '마이페이지에서 리포트용 사진을 등록해 주세요.'}
                >
                  {shareImageDownloading ? '생성 중...' : '발송 이미지'}
                </button>
                {reportTemplateOptions.length > 0 ? (
                  <select
                    className="max-w-[220px] rounded border border-white/30 bg-brand-navy px-2 py-1.5 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                    value={reportTemplateId}
                    onChange={(e) => setReportTemplateId(e.target.value)}
                    disabled={reportTemplateDownloading}
                    title="운영자 전용 리포트 템플릿"
                  >
                    <option value="">운영 기본 리포트</option>
                    {reportTemplateOptions.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.effectiveSelectionLabel || template.title}
                      </option>
                    ))}
                  </select>
                ) : null}
                <button
                  type="button"
                  className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={downloadReportTemplateImage}
                  disabled={presentationQuery.isLoading || presentationQuery.isError || reportTemplateDownloading}
                >
                  {reportTemplateDownloading ? '생성 중...' : '템플릿 리포트'}
                </button>
                {reportHtmlTemplateOptions.length > 0 ? (
                  <select
                    className="max-w-[220px] rounded border border-white/30 bg-brand-navy px-2 py-1.5 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                    value={reportHtmlTemplateId}
                    onChange={(e) => setReportHtmlTemplateId(e.target.value)}
                    disabled={reportHtmlDownloading}
                    title="운영자 전용 HTML 리포트 템플릿"
                  >
                    <option value="">운영 기본 HTML</option>
                    {reportHtmlTemplateOptions.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.effectiveSelectionLabel || template.title}
                      </option>
                    ))}
                  </select>
                ) : null}
                <button
                  type="button"
                  className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={downloadReportTemplateHtml}
                  disabled={presentationQuery.isLoading || presentationQuery.isError || reportHtmlDownloading || reportHtmlTemplateOptions.length === 0}
                  title={reportHtmlTemplateOptions.length > 0 ? '운영자 전용 HTML/HYBRID 리포트' : '게시된 HTML 템플릿이 없습니다.'}
                >
                  {reportHtmlDownloading ? '생성 중...' : 'HTML 리포트'}
                </button>
              </>
            ) : null}
            {showCardNews ? (
              <button
                type="button"
                className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10 disabled:opacity-50"
                onClick={openCardNews}
                disabled={presentationQuery.isLoading || presentationQuery.isError}
              >
                카드뉴스
              </button>
            ) : null}
            <button
              type="button"
              className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10 disabled:opacity-50"
              onClick={() => setReportOpen(true)}
              disabled={presentationQuery.isLoading || presentationQuery.isError}
            >
              리포트 보기
            </button>
            <button
              type="button"
              className="rounded border border-white/30 px-3 py-1.5 text-xs text-white hover:bg-white/10"
              onClick={onBackToList}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>

      {failedYearLabels.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
          일부 연도 자료는 조회 실패로 계산에서 제외되었습니다.
          {successfulYearLabels.length > 0 ? ` 성공 연도: ${successfulYearLabels.join(', ')}.` : ''}
          {' '}제외 연도: {failedYearLabels.join(', ')}.
        </div>
      ) : null}

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block min-w-[160px] text-sm">
            <span className="mb-1 block font-medium text-slate-700">실비 세대</span>
            <select
              className="w-full rounded border px-2 py-1.5 text-sm"
              value={filters.generation}
              onChange={(e) => setFilters((prev) => ({ ...prev, generation: e.target.value }))}
            >
              {generations.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </label>
          <label className="flex min-h-[38px] items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 shrink-0 rounded border-slate-300"
              checked={filters.excludeGlasses}
              onChange={(e) => setFilters((prev) => ({ ...prev, excludeGlasses: e.target.checked }))}
            />
            <span className="text-slate-700">안경·콘텍트렌즈 제외</span>
          </label>
          <label className="flex min-h-[38px] items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 shrink-0 rounded border-slate-300"
              checked={filters.excludePharmacyBelowThreshold}
              onChange={(e) => setFilters((prev) => ({ ...prev, excludePharmacyBelowThreshold: e.target.checked }))}
            />
            <span className="text-slate-700">청구 기준 미달 제외 (약국 8천·통원 1만 미만)</span>
          </label>
          <label className="flex min-h-[38px] items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 shrink-0 rounded border-slate-300"
              checked={filters.includeFamily}
              onChange={(e) => setFilters((prev) => ({ ...prev, includeFamily: e.target.checked }))}
            />
            <span className="text-slate-700">가족 의료비·수령 포함</span>
          </label>
        </div>
        <div className="mt-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-slate-700">
          숨은보험금 가능성은 국세청 의료비 공제자료 기준의 참고 데이터입니다.
          기본은 대상자명이 고객명과 일치하는 본인 의료비·실손 수령만 계산합니다.
          가족의 카드나 가족 연말정산 자료에 잡힌 의료비는 고객 기준 조회에서 보이지 않을 수 있습니다.
        </div>
        <details className="group mt-3 rounded-lg border border-slate-100 bg-slate-50">
          <summary className="cursor-pointer list-none px-3 py-2.5 text-xs font-medium text-slate-800 marker:content-none [&::-webkit-details-marker]:hidden">
            <span className="flex items-center justify-between gap-2">
              <span>세대별 추정 청구 계산</span>
              <span className="font-normal text-slate-400 group-open:hidden">펼치기</span>
              <span className="font-normal text-slate-400 hidden group-open:inline">접기</span>
            </span>
          </summary>
          <div className="border-t border-slate-200 px-3 py-3 text-xs text-slate-600">
            <p className="text-slate-600">
              <span className="font-medium text-brand-navy">{selectedBasis.label}</span>
              {' · '}
              {selectedBasis.period}
            </p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>{selectedBasis.estimate}</li>
              <li>{selectedBasis.pharmacyExclude}</li>
              <li>미달 제외 해제 시 목록·의료비 합계에는 포함되나, 추정 청구 가능액에서는 공제됩니다</li>
              <li>기관명·구분으로 병·의원과 약국을 구분 · 안경·렌즈는 필터로 제외 가능</li>
            </ul>
            <div className="mt-3 border-t border-slate-200 pt-3">
              <div className="font-medium text-slate-700">다른 세대</div>
              <ul className="mt-2 space-y-2">
                {generationBasisList.filter((g) => g.value !== filters.generation).map((g) => (
                  <li key={g.value}>
                    <span className="font-medium text-slate-700">{g.label}</span>
                    <span className="text-slate-500"> ({g.period})</span>
                    <div className="mt-0.5">{g.estimate} · {g.pharmacyExclude}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </details>
      </section>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label="보험료 합계" amount={totals.insurancePremium} sub={`3개년 합계 · ${counts.premiums ?? 0}건`} />
        <SummaryCard label="의료비 합계" amount={totals.medicalExpense} sub={`3개년 합계 · 진료·약국 ${totals.visitCount ?? 0}건`} />
        <SummaryCard label="실손 수령 합계" amount={totals.reimbursement} sub={`3개년 합계 · ${counts.reimbursements ?? 0}건`} />
      </div>

      {presentationQuery.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          결과를 불러오는 중…
        </div>
      ) : presentationQuery.isError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-8 text-center text-sm text-rose-700">
          결과 표시 데이터를 불러오지 못했습니다.
        </div>
      ) : (
      <>
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
          <SummaryCard
            className="col-span-1 lg:col-span-2"
            compact
            emphasis
            heroValue
            label="숨은보험금 가능성"
            value={guide.estimatedClaimable > 0 ? possibilityLabel : '-'}
            toneClass={guide.estimatedClaimable > 0 ? possibilityTone : 'border-slate-200 bg-white'}
          />
          <div className="col-span-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm lg:col-span-4">
            <div className="flex h-full flex-col gap-3 sm:flex-row sm:items-stretch">
              <div className="shrink-0 sm:min-w-[120px] sm:pr-3">
                <div className="text-sm font-medium text-slate-500">추정 청구 가능액</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">{formatWon(guide.estimatedClaimable)}</div>
              </div>
              {claimableBreakdown.lines.length > 0 ? (
                <div className="min-w-0 flex-1 space-y-1 border-slate-100 sm:border-l sm:pl-4">
                  <div className="text-sm font-medium text-slate-500">제외·공제 내역</div>
                  {claimableBreakdown.lines.map((line) => (
                    <div key={line.key} className="flex items-center justify-between gap-2 text-sm text-slate-600">
                      <span className="truncate">- {line.label}</span>
                      <span className="shrink-0 font-medium text-slate-700">{formatWon(line.amount)}</span>
                    </div>
                  ))}
                  {claimableBreakdown.totalReduction > 0 ? (
                    <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-1 text-sm font-medium text-slate-700">
                      <span>합계</span>
                      <span className="shrink-0">{formatWon(claimableBreakdown.totalReduction)}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <SummaryCard
            className="col-span-1 lg:col-span-2"
            emphasis
            label="실제 실손 수령액"
            amount={guide.reimbursementTotal}
          />
          <SummaryCard
            className="col-span-1 lg:col-span-2"
            emphasis
            label="실손 수령 비율"
            value={claimRatio != null ? `${claimRatio}%` : '-'}
            sub={generationLabel}
          />
          <SummaryCard
            className="col-span-1 lg:col-span-2"
            emphasis
            label="미청구 가능성 금액"
            amount={guide.unclaimedHint}
          />
        </div>
        {guide.estimatedClaimable > 0 ? (
          <p className="mt-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            {generationLabel} 기준 추정 청구 가능액 {formatWon(guide.estimatedClaimable)} 대비
            실제 실손 수령 {formatWon(guide.reimbursementTotal)}은 약 <strong>{claimRatio ?? 0}%</strong> 수준입니다.
            {guide.unclaimedHint > 0 ? (
              <> 미청구 가능성이 있는 금액은 약 <strong>{formatWon(guide.unclaimedHint)}</strong>입니다. </>
            ) : (
              <> 현재 필터 기준으로는 추가 청구 여지가 크지 않아 보입니다.</>
            )}
          </p>
        ) : (
          <p className="mt-4 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            필터 조건에 해당하는 의료비 내역이 없습니다.
          </p>
        )}
        
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'medical', label: '의료비' },
            { id: 'reimbursements', label: '실손 수령' },
            { id: 'premiums', label: '보험료' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                resultTab === tab.id
                  ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
              }`}
              onClick={() => setResultTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <ResultList
            key={`${resultTab}-${sortBy}`}
            items={listItems}
            sortBy={sortBy}
            yearlyColumns={sortBy === 'date'}
            years={targetYears}
            emptyLabel={
              resultTab === 'premiums'
                ? '보험료 내역이 없습니다.'
                : resultTab === 'reimbursements'
                  ? '실손 수령 내역이 없습니다.'
                  : '의료비 내역이 없습니다.'
            }
          />
        </div>
      </section>
      </>
      )}

      <HiddenInsuranceReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        jobId={job?.id}
        customerName={customerName}
        filters={filters}
        sortBy={sortBy}
        showCardNews={showCardNews}
        showShareImageButton={showShareImageButton}
        canDownloadShareImage={canDownloadShareImage}
        onDownloadShareImage={downloadShareImage}
        shareImageDownloading={shareImageDownloading}
      />
    </div>
  )
}

export default function HiddenInsuranceMoneyPage() {
  const { show } = useToast()
  const { hasAnyRole, user } = useAuth()
  const isOperator = hasAnyRole([Roles.OPERATOR])
  const showShareImageButton = isOperator
  const shareProfileFileId = hiddenInsuranceShareProfileFileId(user)
  const canDownloadShareImage = showShareImageButton && Boolean(shareProfileFileId)
  const shareImageAdvisor = useMemo(() => ({
    name: user?.name || user?.displayName || '',
    phone: user?.phoneNumber || user?.phone || user?.mobilePhone || '',
  }), [user])
  const isGaScopeAdmin = !isOperator && hasAnyRole([
    Roles.GA_LEADER,
    Roles.GA_MANAGER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_OTHER_ADMIN,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
  ])
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [form, setForm] = useState(DEFAULT_FORM)
  const [dataConsents, setDataConsents] = useState({})
  const consentBundleQuery = useHealthDataConsentBundle('hidden-insurance')
  const consentBundle = consentBundleQuery.data
  const consentItems = consentBundle?.items ?? []
  const [activeJobId, setActiveJobId] = useState(null)
  const [view, setView] = useState('form')
  const [activeTab, setActiveTab] = useState('new')
  const [historySearch, setHistorySearch] = useState('')
  const [operatorLoadMode, setOperatorLoadMode] = useState('MINE')
  const [operatorOrgId, setOperatorOrgId] = useState('')
  const [operatorOrgOptions, setOperatorOrgOptions] = useState([])
  const [operatorOrgLoading, setOperatorOrgLoading] = useState(false)
  const operatorReady = !isOperator || operatorLoadMode === 'ALL' || operatorLoadMode === 'MINE' || Boolean(operatorOrgId)
  const [gaLoadMode, setGaLoadMode] = useState('MINE')
  const [gaOrgId, setGaOrgId] = useState('')
  const [gaOrgOptions, setGaOrgOptions] = useState([])
  const [gaOrgLoading, setGaOrgLoading] = useState(false)
  const gaReady = !isGaScopeAdmin || gaLoadMode === 'ALL' || gaLoadMode === 'MINE' || Boolean(gaOrgId)
  const [pollAttemptCount, setPollAttemptCount] = useState(0)
  const [failInfoJob, setFailInfoJob] = useState(null)
  const [forceRefreshNext, setForceRefreshNext] = useState(false)
  const pollBusyRef = useRef(false)
  const pollTimerRef = useRef(null)
  const activePollJobRef = useRef(null)
  const startSubmitLockRef = useRef(false)
  const crmPrefillFromUrlRef = useRef(false)
  const openLookupHandledRef = useRef(false)

  useEffect(() => {
    if (consentItems.length) {
      setDataConsents(createInitialHealthDataConsents(consentItems))
    }
  }, [consentBundle?.variant, consentBundle?.bundleVersion, consentItems.length])

  useEffect(() => {
    if (!isOperator) return
    let ignore = false
    async function loadOperatorOrgs() {
      setOperatorOrgLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'all' } })
        const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
        if (!ignore) setOperatorOrgOptions(Array.isArray(payload) ? payload : [])
      } catch {
        if (!ignore) setOperatorOrgOptions([])
      } finally {
        if (!ignore) setOperatorOrgLoading(false)
      }
    }
    loadOperatorOrgs()
    return () => { ignore = true }
  }, [isOperator])

  useEffect(() => {
    if (!isGaScopeAdmin) return
    let ignore = false
    async function loadGaOrgs() {
      setGaOrgLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
        const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
        const list = Array.isArray(payload) ? payload : []
        const gaOnly = list.filter((o) => String(o?.type || '').toUpperCase() === 'GA')
        if (!ignore) setGaOrgOptions(gaOnly)
      } catch {
        if (!ignore) setGaOrgOptions([])
      } finally {
        if (!ignore) setGaOrgLoading(false)
      }
    }
    loadGaOrgs()
    return () => { ignore = true }
  }, [isGaScopeAdmin])

  useEffect(() => {
    if (crmPrefillFromUrlRef.current) return
    const raw = searchParams.get('customerRefId')
    if (!raw) return
    const id = Number(String(raw).trim())
    if (!Number.isFinite(id) || id <= 0) return
    crmPrefillFromUrlRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
        const data = res?.data?.data ?? res?.data
        if (!data) {
          crmPrefillFromUrlRef.current = false
          return
        }
        setForm((prev) => ({
          ...prev,
          customerRefId: String(id),
          customerName: data.customerName != null ? String(data.customerName).trim() : prev.customerName,
          phoneNumber: data.customerPhone != null ? formatPhoneDigitsKo(data.customerPhone) : prev.phoneNumber,
        }))
        setActiveTab('new')
        show('고객관리에서 이름·연락처·고객 연결을 불러왔습니다.', 'success')
        const next = new URLSearchParams(searchParams)
        next.delete('customerRefId')
        next.delete('customerName')
        setSearchParams(next, { replace: true })
      } catch {
        crmPrefillFromUrlRef.current = false
      }
    })()
  }, [searchParams, setSearchParams, show])

  useEffect(() => {
    if (openLookupHandledRef.current) return
    const raw = searchParams.get('openLookupId')
    if (!raw) return
    const lookupId = Number(String(raw).trim())
    if (!Number.isFinite(lookupId) || lookupId <= 0) return
    openLookupHandledRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/medical-reimbursement-lookups/${lookupId}`)
        const data = res.data?.data || res.data
        if (!data?.id) {
          openLookupHandledRef.current = false
          return
        }
        setActiveJobId(data.id)
        queryClient.setQueryData(['hidden-insurance-job', data.id], data)
        if (data.customerRefId) {
          setForm((prev) => ({
            ...prev,
            customerRefId: String(data.customerRefId),
            customerName: data.customerName || prev.customerName,
          }))
        }
        if (data.status === 'COMPLETED') {
          setView('result')
        } else if (data.status === 'ANALYSIS_RUNNING') {
          setView('processing')
        } else if (data.status === 'AUTH_WAITING' || data.status === 'AUTH_REQUESTED') {
          setView('auth')
        } else {
          setActiveTab('history')
          setView('form')
        }
        const next = new URLSearchParams(searchParams)
        next.delete('openLookupId')
        setSearchParams(next, { replace: true })
      } catch (e) {
        openLookupHandledRef.current = false
        show(getApiErrorMessage(e, '조회 결과를 불러오지 못했습니다.'), 'error')
      }
    })()
  }, [queryClient, searchParams, setSearchParams, show])

  const identityDigitsUiDisplay = formatIdentityDigitsForUi(form.identity)

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'phoneNumber') {
      setForm((prev) => ({ ...prev, customerRefId: '', phoneNumber: formatPhoneDigitsKo(value) }))
      return
    }
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const pollAttemptsRef = useRef(0)

  const stopAuthPolling = useCallback(() => {
    activePollJobRef.current = null
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollBusyRef.current = false
  }, [])

  const jobQuery = useQuery({
    queryKey: ['hidden-insurance-job', activeJobId],
    queryFn: async () => {
      const res = await api.get(`/medical-reimbursement-lookups/${activeJobId}`)
      return res.data?.data || res.data
    },
    enabled: activeJobId != null && (view === 'auth' || view === 'processing'),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'ANALYSIS_RUNNING') return 3000
      return false
    },
  })

  const runPollAuthOnce = useCallback(async (jobId) => {
    if (pollBusyRef.current) return null
    pollBusyRef.current = true
    try {
      const res = await api.post(`/medical-reimbursement-lookups/${jobId}/poll-auth`)
      const data = res.data?.data || res.data
      queryClient.setQueryData(['hidden-insurance-job', jobId], data)
      return data
    } finally {
      pollBusyRef.current = false
    }
  }, [queryClient])

  const startAuthPolling = useCallback((jobId) => {
    if (activePollJobRef.current === jobId && pollTimerRef.current != null) return
    stopAuthPolling()
    activePollJobRef.current = jobId
    pollAttemptsRef.current = 0
    setPollAttemptCount(0)

    const scheduleNext = (delayMs) => {
      pollTimerRef.current = window.setTimeout(runPollCycle, delayMs)
    }

    const runPollCycle = async () => {
      if (activePollJobRef.current !== jobId) return
      if (pollBusyRef.current) {
        scheduleNext(AUTH_POLL_INTERVAL_MS)
        return
      }

      pollAttemptsRef.current += 1
      setPollAttemptCount(pollAttemptsRef.current)
      if (pollAttemptsRef.current > AUTH_POLL_MAX_ATTEMPTS) {
        stopAuthPolling()
        show('간편인증 대기 시간이 초과되었습니다.', 'error')
        setView('form')
        return
      }

      try {
        const data = await runPollAuthOnce(jobId)
        if (activePollJobRef.current !== jobId) return
        if (!data) {
          scheduleNext(AUTH_POLL_INTERVAL_MS)
          return
        }

        if (data.status === 'ANALYSIS_RUNNING') {
          stopAuthPolling()
          setView('processing')
          return
        }
        if (data.status === 'COMPLETED') {
          stopAuthPolling()
          setView('result')
          show('조회가 완료되었습니다.', 'success')
          queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
          return
        }
        if (data.status === 'FAILED') {
          stopAuthPolling()
          setView('form')
          queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
          setFailInfoJob(data)
          return
        }
      } catch (e) {
        if (pollAttemptsRef.current >= AUTH_POLL_MAX_ATTEMPTS) {
          stopAuthPolling()
          show(getApiErrorMessage(e, '인증 확인에 실패했습니다.'), 'error')
          setView('form')
          return
        }
      }

      scheduleNext(AUTH_POLL_INTERVAL_MS)
    }

    scheduleNext(AUTH_POLL_INITIAL_DELAY_MS)
  }, [queryClient, runPollAuthOnce, show, stopAuthPolling])

  useEffect(() => () => stopAuthPolling(), [stopAuthPolling])

  useEffect(() => {
    if (view === 'auth' && activeJobId != null) {
      startAuthPolling(activeJobId)
    } else if (view !== 'auth') {
      stopAuthPolling()
    }
  }, [view, activeJobId, startAuthPolling, stopAuthPolling])

  useEffect(() => {
    const job = activeJobId != null
      ? (queryClient.getQueryData(['hidden-insurance-job', activeJobId]) || jobQuery.data)
      : null
    const status = job?.status
    if (!activeJobId || !status) return

    if (status === 'ANALYSIS_RUNNING' && view === 'auth') {
      setView('processing')
    } else if (status === 'COMPLETED' && (view === 'processing' || view === 'auth')) {
      setView('result')
      queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
    } else if (status === 'FAILED' && (view === 'auth' || view === 'processing')) {
      setView('form')
      setFailInfoJob(job)
      queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
    }
  }, [activeJobId, jobQuery.data, view, queryClient])

  const linkedCustomerRefId = form.customerRefId ? String(form.customerRefId).trim() : ''
  const historyScopeKey = useMemo(() => {
    if (isOperator) return { operatorLoadMode, operatorOrgId }
    if (isGaScopeAdmin) return { gaLoadMode, gaOrgId }
    return {}
  }, [isOperator, operatorLoadMode, operatorOrgId, isGaScopeAdmin, gaLoadMode, gaOrgId])
  const historyReady = isOperator ? operatorReady : (isGaScopeAdmin ? gaReady : true)

  const billingMeQuery = useQuery({
    queryKey: ['billing-me'],
    queryFn: async () => {
      const res = await api.get('/billing/me', { __silent: true })
      return res.data?.data ?? res.data
    },
    staleTime: 60_000,
    enabled: activeTab === 'new',
  })

  const hiddenInsuranceBillingGate = useMemo(
    () => resolveFeatureBillingGate(billingMeQuery.data, FEATURE_BILLING_CODES.HIDDEN_INSURANCE),
    [billingMeQuery.data],
  )

  const historyQuery = useQuery({
    queryKey: [
      'hidden-insurance-lookups',
      linkedCustomerRefId || 'all',
      historySearch.trim(),
      historyScopeKey,
    ],
    queryFn: async () => {
      const params = {}
      if (linkedCustomerRefId) params.customerRefId = linkedCustomerRefId
      if (historySearch.trim()) params.q = historySearch.trim()
      if (isOperator) {
        if (operatorLoadMode === 'ALL') params.viewAll = true
        if (operatorLoadMode === 'BY_ORG' && operatorOrgId) params.orgId = operatorOrgId
      } else if (isGaScopeAdmin) {
        if (gaLoadMode === 'ALL') {
          params.viewAll = true
        } else if (gaLoadMode === 'BY_ORG' && gaOrgId) {
          params.orgId = gaOrgId
        }
      }
      const res = await api.get('/medical-reimbursement-lookups', { params })
      const data = res.data?.data || res.data
      return Array.isArray(data) ? data : []
    },
    enabled: historyReady && (activeTab === 'history' || view === 'result' || view === 'processing' || view === 'auth'),
  })

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase()
    const rows = historyQuery.data || []
    if (!q) return rows
    return rows.filter((job) => String(job.customerName || '').toLowerCase().includes(q))
  }, [historyQuery.data, historySearch])
  const currentUserId = user?.id != null ? String(user.id) : ''
  const showHistoryOwnerInfo = isOperator || isGaScopeAdmin
  const canManageHistoryJob = useCallback((job) => {
    if (job?.canManage === true || job?.isOwn === true) return true
    if (job?.canManage === false || job?.isOwn === false) return false
    if (currentUserId && job?.createdByUserId != null) {
      return String(job.createdByUserId) === currentUserId
    }
    return true
  }, [currentUserId])

  const openFailInfo = useCallback(async (job) => {
    if (!job?.id) return
    try {
      const res = await api.get(`/medical-reimbursement-lookups/${job.id}`)
      const data = res.data?.data || res.data
      setFailInfoJob({ ...job, ...data })
    } catch {
      setFailInfoJob(job)
    }
  }, [])

  const handleRetryLookup = useCallback((job) => {
    setForm({
      ...DEFAULT_FORM,
      customerRefId: job?.customerRefId ? String(job.customerRefId) : '',
      customerName: job?.customerName || '',
    })
    setForceRefreshNext(true)
    setFailInfoJob(null)
    setActiveTab('new')
    setView('form')
    show('동일 고객으로 다시 조회합니다. 주민번호·연락처를 입력한 뒤 조회 시작을 눌러 주세요.', 'success')
  }, [show])

  const startMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/medical-reimbursement-lookups', payload)
      return res.data?.data || res.data
    },
    onSuccess: (data) => {
      setActiveJobId(data.id)
      queryClient.setQueryData(['hidden-insurance-job', data.id], data)
      queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
      queryClient.invalidateQueries({ queryKey: ['billing-me'] })
      if (data.status === 'COMPLETED') {
        setView('result')
        show(data.reused ? '최근 조회 결과를 불러왔습니다.' : '조회가 완료되었습니다.', 'success')
        return
      }
      if (data.status === 'ANALYSIS_RUNNING') {
        setActiveTab('history')
        setView('processing')
        show('자료 분석을 진행하고 있습니다.', 'success')
        return
      }
      setActiveTab('history')
      setView('auth')
      show('간편인증을 진행해 주세요.', 'success')
    },
    onError: (e) => {
      show(getApiErrorMessage(e, '조회 요청에 실패했습니다.'), 'error')
    },
    onSettled: () => {
      startSubmitLockRef.current = false
    },
  })

  const identityInputDisabled = view === 'auth' || view === 'processing' || startMutation.isPending

  const handleIdentityChange = useCallback((e) => {
    if (identityInputDisabled) return
    const digits = normalizeIdentityDigits(e.target.value)
    setForm((prev) => ({ ...prev, identity: digits }))
  }, [identityInputDisabled])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (startSubmitLockRef.current || startMutation.isPending || view === 'auth' || view === 'processing') return

    const nameTrim = String(form.customerName || '').trim()
    const identityDigits = normalizeIdentityDigits(form.identity)
    const phoneDigits = String(form.phoneNumber || '').replace(/[^0-9]/g, '')

    if (!nameTrim) {
      show('고객명을 입력해 주세요.', 'error')
      return
    }
    if (identityDigits.length !== MAX_IDENTITY_DIGITS) {
      show('주민등록번호는 숫자 13자리로 입력해 주세요.', 'error')
      return
    }
    if (!(phoneDigits.length >= 10 && phoneDigits.length <= MAX_PHONE_DIGITS)) {
      show(`연락처는 숫자 10~${MAX_PHONE_DIGITS}자리로 입력해 주세요.`, 'error')
      return
    }
    if (!allHealthDataConsentsChecked(consentItems, dataConsents)) {
      show('필수 동의 항목을 모두 확인해 주세요.', 'error')
      return
    }
    if (!hiddenInsuranceBillingGate.canUse) {
      show(featureBillingBlockMessage(hiddenInsuranceBillingGate), 'error')
      return
    }

    startSubmitLockRef.current = true
    startMutation.mutate({
      customerRefId: form.customerRefId ? Number(form.customerRefId) : null,
      customerName: nameTrim,
      identity: identityDigits,
      phoneNumber: phoneDigits,
      telecom: form.telecom,
      authProvider: form.authProvider,
      forceRefresh: forceRefreshNext,
      ...buildHealthDataConsentPayload(consentBundle, dataConsents),
    })
    setForceRefreshNext(false)
  }

  const handleBackToListFromResult = useCallback(() => {
    setView('form')
    setActiveTab('history')
  }, [])

  const handleReset = useCallback(() => {
    stopAuthPolling()
    setView('form')
    setActiveJobId(null)
    setForm(DEFAULT_FORM)
    setDataConsents(createInitialHealthDataConsents(consentItems))
    setActiveTab('new')
  }, [stopAuthPolling])

  const deleteLookupMutation = useMutation({
    mutationFn: async (jobId) => {
      await api.delete(`/medical-reimbursement-lookups/${jobId}`)
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['hidden-insurance-lookups'] })
      queryClient.removeQueries({ queryKey: ['hidden-insurance-job', jobId] })
      if (activeJobId === jobId) {
        handleReset()
      }
      show('조회 결과를 삭제했습니다.', 'success')
    },
    onError: (e) => {
      show(getApiErrorMessage(e, '조회 결과를 삭제하지 못했습니다.'), 'error')
    },
  })

  const handleDeleteLookup = useCallback((job) => {
    if (!job?.id || deleteLookupMutation.isLoading) return
    const label = job.customerName || '조회'
    const ok = window.confirm(`${label} 조회 결과를 삭제할까요?\n삭제 후에는 다시 확인할 수 없습니다.`)
    if (!ok) return
    deleteLookupMutation.mutate(job.id)
  }, [deleteLookupMutation])

  const openHistoryJob = (job) => {
    if (job.status !== 'COMPLETED') {
      show('완료된 조회만 결과를 볼 수 있습니다.', 'error')
      return
    }
    setActiveJobId(job.id)
    queryClient.setQueryData(['hidden-insurance-job', job.id], job)
    setView('result')
  }

  const resumeProcessingJob = (job) => {
    if (job.status !== 'ANALYSIS_RUNNING') return
    if (view === 'processing' && activeJobId === job.id) return
    setActiveJobId(job.id)
    setForm((prev) => ({
      ...prev,
      customerRefId: job.customerRefId ? String(job.customerRefId) : prev.customerRefId,
      customerName: job.customerName || prev.customerName,
    }))
    queryClient.setQueryData(['hidden-insurance-job', job.id], job)
    setActiveTab('history')
    setView('processing')
  }

  const resumeAuthJob = (job) => {
    if (job.status !== 'AUTH_WAITING' && job.status !== 'AUTH_REQUESTED') return
    if (view === 'auth' && activeJobId === job.id) return
    setActiveJobId(job.id)
    setForm((prev) => ({
      ...prev,
      customerRefId: job.customerRefId ? String(job.customerRefId) : prev.customerRefId,
      customerName: job.customerName || prev.customerName,
    }))
    queryClient.setQueryData(['hidden-insurance-job', job.id], job)
    setActiveTab('history')
    setView('auth')
  }

  const currentJob = activeJobId != null
    ? (queryClient.getQueryData(['hidden-insurance-job', activeJobId]) || jobQuery.data)
    : null
  const displayName = currentJob?.customerName || form.customerName

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">숨은보험금찾기</h1>
        {linkedCustomerRefId ? (
          <p className="mt-1 text-sm text-slate-600">
            연결 고객:{' '}
            <Link
              className="font-medium text-brand-navy hover:underline"
              to={`/support/customers/${linkedCustomerRefId}`}
            >
              {displayName || '고객 상세'}
            </Link>
          </p>
        ) : null}
      </header>

      {view === 'result' && currentJob?.status === 'COMPLETED' ? (
        <LookupResultView
          job={currentJob}
          customerName={displayName}
          onBackToList={handleBackToListFromResult}
          showCardNews={isOperator && SHOW_CUSTOMER_CARD_NEWS_BUTTONS}
          showShareImageButton={showShareImageButton}
          canDownloadShareImage={canDownloadShareImage}
          shareProfileFileId={shareProfileFileId}
          advisor={shareImageAdvisor}
        />
      ) : (
        <>
          {view === 'auth' ? (
            <div className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <div className="font-semibold">간편인증 진행 중</div>
              <p className="mt-1">
                카카오톡으로 간편인증을 완료해 주세요. 인증이 끝나면 3개년 자료 분석을 시작합니다.
              </p>
              <div className="mt-2 text-xs text-sky-700">
                상태: {jobStatusLabel(currentJob?.status)} · 확인 {pollAttemptCount}/{AUTH_POLL_MAX_ATTEMPTS}
              </div>
            </div>
          ) : null}

          {view === 'processing' ? (
            <div className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <div className="font-semibold">분석 진행 중</div>
              <p className="mt-1">
                인증이 완료되어 3개년 자료를 조회·분석하고 있습니다. 잠시만 기다려 주세요.
              </p>
              <div className="mt-2 text-xs text-sky-700">
                상태: {jobStatusLabel(currentJob?.status)}
              </div>
            </div>
          ) : null}

          <div className="border-b flex gap-4 text-sm">
            <button
              type="button"
              className={`pb-2 border-b-2 ${activeTab === 'new' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'}`}
              onClick={() => setActiveTab('new')}
            >
              신규 조회
            </button>
            <button
              type="button"
              className={`pb-2 border-b-2 ${activeTab === 'history' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'}`}
              onClick={() => setActiveTab('history')}
            >
              조회 이력
            </button>
          </div>

          {activeTab === 'new' ? (
            <HealthIntakeFormLayout
              serviceLabel={HEALTH_DATA_CONSENT_VARIANTS['hidden-insurance'].serviceLabel}
              quotaSlot={(
                <FeatureBillingQuotaSlot gate={hiddenInsuranceBillingGate} />
              )}
              onSubmit={handleSubmit}
              identityPanel={(
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block font-medium text-slate-700">고객명</label>
                    <ManagedCustomerAutocomplete
                      name="customerName"
                      value={form.customerName}
                      onValueChange={(next) => setForm((prev) => ({ ...prev, customerRefId: '', customerName: next }))}
                      selectedCustomerId={form.customerRefId}
                      onSelectCustomer={(customer) => {
                        setForm((prev) => ({
                          ...prev,
                          customerRefId: String(customer.id),
                          customerName: customer.customerName != null ? String(customer.customerName).trim() : prev.customerName,
                          phoneNumber: customer.customerPhone ? formatPhoneDigitsKo(customer.customerPhone) : prev.phoneNumber,
                        }))
                      }}
                      onClearCustomer={() => setForm((prev) => ({ ...prev, customerRefId: '' }))}
                      inputClassName="w-full rounded border px-2 py-1.5"
                      required
                      disabled={identityInputDisabled}
                    />
                    {linkedCustomerRefId ? (
                      <p className="mt-1 text-xs text-slate-500">
                        <Link className="text-brand-navy hover:underline" to={`/support/customers/${linkedCustomerRefId}`}>
                          고객 상세
                        </Link>
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block font-medium text-slate-700">주민등록번호</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="w-full rounded border px-2 py-1.5 font-mono"
                      placeholder="숫자 13자리"
                      value={identityDigitsUiDisplay}
                      onChange={handleIdentityChange}
                      required
                      disabled={identityInputDisabled}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block font-medium text-slate-700">인증수단</label>
                    <select
                      name="authProvider"
                      value={form.authProvider}
                      onChange={handleChange}
                      className="w-full rounded border px-2 py-1.5"
                      disabled={identityInputDisabled}
                    >
                      <option value="KAKAO">카카오톡(간편인증)</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block font-medium text-slate-700">통신사</label>
                    <select
                      name="telecom"
                      value={form.telecom}
                      onChange={handleChange}
                      className="w-full rounded border px-2 py-1.5"
                      disabled={identityInputDisabled}
                    >
                      <option value="0">SKT</option>
                      <option value="1">KT</option>
                      <option value="2">LG U+</option>
                      <option value="3">알뜰폰(SKT)</option>
                      <option value="4">알뜰폰(KT)</option>
                      <option value="5">알뜰폰(LG U+)</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block font-medium text-slate-700">연락처</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      className="w-full rounded border px-2 py-1.5"
                      placeholder="010-1234-5678"
                      required
                      disabled={identityInputDisabled}
                    />
                  </div>
                </div>
              )}
              consentPanel={(
                <HealthDataConsentFields
                  items={consentItems}
                  checked={dataConsents}
                  onChange={setDataConsents}
                  disabled={identityInputDisabled}
                  loading={consentBundleQuery.isLoading}
                />
              )}
              footerActions={(
                <>
                  <button
                    type="submit"
                    className="rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-sky disabled:opacity-60"
                    disabled={startMutation.isPending || view === 'auth' || view === 'processing' || consentBundleQuery.isLoading || !allHealthDataConsentsChecked(consentItems, dataConsents) || !hiddenInsuranceBillingGate.canUse}
                  >
                    {startMutation.isPending
                      ? '요청 중…'
                      : view === 'auth'
                        ? '인증 진행 중…'
                        : view === 'processing'
                          ? '분석 진행 중…'
                          : '조회 시작'}
                  </button>
                  {view === 'auth' || view === 'processing' ? (
                    <button
                      type="button"
                      className="rounded border px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={handleReset}
                    >
                      취소
                    </button>
                  ) : null}
                </>
              )}
            />
          ) : (
            <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-slate-900">
                  {linkedCustomerRefId ? '고객 조회 이력' : '조회 이력'}
                </h2>
                {linkedCustomerRefId ? (
                  <button
                    type="button"
                    className="rounded border px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                    onClick={() => setForm((prev) => ({ ...prev, customerRefId: '' }))}
                  >
                    전체 이력 보기
                  </button>
                ) : null}
              </div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  {isOperator ? (
                    <>
                      <select
                        className="rounded border px-2 py-1 text-sm"
                        value={operatorLoadMode}
                        onChange={(e) => {
                          const v = e.target.value || 'MINE'
                          if (v === 'ALL') {
                            const ok = window.confirm('전체보기는 전체 조회 이력을 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                            if (!ok) return
                            setOperatorOrgId('')
                          } else if (v === 'MINE') {
                            setOperatorOrgId('')
                          }
                          setOperatorLoadMode(v)
                        }}
                        title="운영자: 조회 모드"
                      >
                        <option value="MINE">내것만 보기</option>
                        <option value="BY_ORG">조직별</option>
                        <option value="ALL">전체보기</option>
                      </select>
                      <select
                        className="rounded border px-2 py-1 text-sm"
                        value={operatorOrgId}
                        disabled={operatorLoadMode !== 'BY_ORG' || operatorOrgLoading}
                        onChange={(e) => setOperatorOrgId(e.target.value)}
                        title="운영자: 조직 선택"
                      >
                        <option value="">{operatorOrgLoading ? '조직 불러오는 중...' : '조직 선택(필수)'}</option>
                        {operatorOrgOptions.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </>
                  ) : null}
                  {isGaScopeAdmin ? (
                    <>
                      <select
                        className="rounded border px-2 py-1 text-sm"
                        value={gaLoadMode}
                        onChange={(e) => {
                          const v = e.target.value || 'MINE'
                          if (v === 'MINE') {
                            setGaOrgId('')
                          } else if (v === 'ALL') {
                            const ok = window.confirm('전체보기는 조직 내 조회 이력을 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                            if (!ok) return
                            setGaOrgId('')
                          }
                          setGaLoadMode(v)
                        }}
                        title="GA: 조회 범위"
                      >
                        <option value="MINE">내것만 보기</option>
                        <option value="BY_ORG">조직별</option>
                        <option value="ALL">전체보기</option>
                      </select>
                      <select
                        className="rounded border px-2 py-1 text-sm"
                        value={gaOrgId}
                        disabled={gaLoadMode !== 'BY_ORG' || gaOrgLoading}
                        onChange={(e) => setGaOrgId(e.target.value)}
                        title="GA: 조직 선택"
                      >
                        <option value="">{gaOrgLoading ? '조직 불러오는 중...' : '내 조직(전체)'}</option>
                        {gaOrgOptions.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </>
                  ) : null}
                  <input
                    type="text"
                    className="rounded border px-2 py-1 text-sm"
                    placeholder="고객명 검색"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                    onClick={() => historyQuery.refetch()}
                  >
                    새로고침
                  </button>
                </div>
              </div>
              {isOperator && operatorLoadMode === 'BY_ORG' && !operatorOrgId ? (
                <p className="text-xs text-slate-500">운영자는 먼저 조직을 선택한 뒤 목록이 조회됩니다.</p>
              ) : null}
              {isGaScopeAdmin && gaLoadMode === 'BY_ORG' && gaOrgLoading ? (
                <p className="text-xs text-slate-500">조직 옵션을 불러오는 중입니다...</p>
              ) : null}

              {historyQuery.isLoading ? (
                <p className="text-xs text-slate-500">조회 이력을 불러오는 중입니다...</p>
              ) : historyQuery.isError ? (
                <p className="text-xs text-red-500">조회 이력을 불러오지 못했습니다.</p>
              ) : filteredHistory.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">조회 이력이 없습니다.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  {filteredHistory.map((job) => (
                    <div key={job.id} className="rounded border px-3 py-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-slate-900">
                            {job.customerName ? `고객명: ${job.customerName}` : '고객 (이름 미상)'}
                          </div>
                          {job.customerRefId ? (
                            <Link
                              className="text-xs text-brand-navy hover:underline"
                              to={`/support/customers/${job.customerRefId}`}
                            >
                              고객 상세
                            </Link>
                          ) : null}
                          <div className="text-xs text-slate-500">
                            요청일시: {formatApiDateTimeDisplay(job.createdAtDisplay, job.createdAt)}
                          </div>
                          {showHistoryOwnerInfo && (job.createdByName || job.creatorOrgName) ? (
                            <div className="text-xs text-slate-500">
                              {[job.createdByName ? `조회자: ${job.createdByName}` : null, job.creatorOrgName ? `조직: ${job.creatorOrgName}` : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-emerald-600">
                            상태:{' '}
                            {job.status === 'FAILED' ? (
                              <button
                                type="button"
                                className="rounded border px-2 py-0.5 text-xs border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                                onClick={() => openFailInfo(job)}
                                title="실패 사유 보기"
                              >
                                실패
                              </button>
                            ) : (
                              <span className={jobStatusBadgeClass(job.status)}>{jobStatusLabel(job.status)}</span>
                            )}
                          </span>
                          {job.status === 'FAILED' && canManageHistoryJob(job) && (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                              onClick={() => handleRetryLookup(job)}
                            >
                              재시도
                            </button>
                          )}
                          {(job.status === 'AUTH_WAITING' || job.status === 'AUTH_REQUESTED') && canManageHistoryJob(job) && (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                              onClick={() => resumeAuthJob(job)}
                            >
                              인증 확인
                            </button>
                          )}
                          {job.status === 'ANALYSIS_RUNNING' && canManageHistoryJob(job) && (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
                              onClick={() => resumeProcessingJob(job)}
                            >
                              분석 확인
                            </button>
                          )}
                          {job.status === 'COMPLETED' && (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                              onClick={() => openHistoryJob(job)}
                            >
                              결과 조회
                            </button>
                          )}
                          {canManageHistoryJob(job) ? (
                            <button
                              type="button"
                              className="rounded border border-rose-200 px-2 py-0.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                              onClick={() => handleDeleteLookup(job)}
                              disabled={deleteLookupMutation.isLoading}
                            >
                              삭제
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {failInfoJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">실패 사유</div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setFailInfoJob(null)}
              >
                닫기
              </button>
            </div>

            <div className="mt-3 space-y-2 text-sm">
              {(() => {
                const p = parseLookupFailure(failInfoJob.errorMessage)
                return (
                  <>
                    <div className="text-xs text-slate-600">
                      Job ID: <span className="font-mono">{failInfoJob.id}</span> · 상태:{' '}
                      <span className="font-mono">{jobStatusLabel(failInfoJob.status)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="rounded border p-2">
                        <div className="text-slate-500">실패 구간</div>
                        <div className="font-mono text-slate-900">{p.phase || '-'}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-slate-500">CODEF 코드</div>
                        <div className="font-mono text-slate-900">{p.code || '-'}</div>
                      </div>
                    </div>
                    {p.summary ? (
                      <div className="rounded border bg-white p-2 text-xs">
                        <div className="text-slate-500 mb-0.5">요약</div>
                        <div className="text-slate-900">{p.summary}</div>
                      </div>
                    ) : null}
                    <div className="rounded border bg-slate-50 p-3">
                      <div className="text-xs text-slate-500 mb-1">원문(errorMessage)</div>
                      <pre className="whitespace-pre-wrap break-words text-xs font-mono text-slate-900">
                        {String(failInfoJob.errorMessage || '사유를 확인할 수 없습니다.')}
                      </pre>
                    </div>
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                        onClick={async () => {
                          const lines = [
                            '[숨은보험금찾기 실패]',
                            `- jobId: ${failInfoJob.id ?? '-'}`,
                            `- status: ${failInfoJob.status ?? '-'}`,
                            failInfoJob.createdAt ? `- createdAt: ${formatApiDateTimeDisplay(failInfoJob.createdAtDisplay, failInfoJob.createdAt)}` : null,
                            `- phase: ${p.phase || '-'}`,
                            `- codefCode: ${p.code || '-'}`,
                            `- summary: ${p.summary || '-'}`,
                            `- errorMessage: ${p.raw || '-'}`,
                          ].filter(Boolean)
                          const ok = await copyToClipboard(lines.join('\n'))
                          if (ok) show('복사되었습니다. 운영자에게 전달해 주세요.', 'success')
                          else show('복사에 실패했습니다.', 'error')
                        }}
                      >
                        운영 문의용 복사
                      </button>
                    </div>
                  </>
                )
              })()}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                  onClick={() => setFailInfoJob(null)}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky"
                  onClick={() => handleRetryLookup(failInfoJob)}
                >
                  재시도
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
